/**
 * DateRangeCalendar Component
 *
 * Airbnb-style two-month date range picker.
 * Desktop: two months side by side with prev/next navigation.
 * Mobile: two months stacked.
 */

import React, { useState, useCallback } from 'react';
import { CalendarMonth } from './CalendarMonth';
import { useDateRangePicker } from './useDateRangePicker';

interface DateRangeCalendarProps {
  checkIn?: Date | null;
  checkOut?: Date | null;
  onChange: (checkIn: Date | null, checkOut: Date | null) => void;
  blockedDates?: Set<string>;
  minimumStay?: number;
  maximumStay?: number;
}

export const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
  checkIn: initialCheckIn = null,
  checkOut: initialCheckOut = null,
  onChange,
  blockedDates,
  minimumStay,
  maximumStay,
}) => {
  const today = new Date();
  const [baseMonth, setBaseMonth] = useState(() => {
    if (initialCheckIn) {
      return { year: initialCheckIn.getFullYear(), month: initialCheckIn.getMonth() };
    }
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  const { checkIn, checkOut, phase, getDayStatus, handleDayClick, handleDayHover, clearDates } =
    useDateRangePicker({
      blockedDates,
      minimumStay,
      maximumStay,
      initialCheckIn,
      initialCheckOut,
      onChange,
    });

  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setBaseMonth((prev) => {
      let { year, month } = prev;
      if (direction === 'prev') {
        month -= 1;
        if (month < 0) {
          month = 11;
          year -= 1;
        }
      } else {
        month += 1;
        if (month > 11) {
          month = 0;
          year += 1;
        }
      }
      return { year, month };
    });
  }, []);

  // Can't navigate before current month
  const canGoPrev =
    baseMonth.year > today.getFullYear() ||
    (baseMonth.year === today.getFullYear() && baseMonth.month > today.getMonth());

  // Second month
  let secondYear = baseMonth.year;
  let secondMonth = baseMonth.month + 1;
  if (secondMonth > 11) {
    secondMonth = 0;
    secondYear += 1;
  }

  const phaseText =
    phase === 'selecting-checkin'
      ? 'Select check-in date'
      : phase === 'selecting-checkout'
        ? 'Select checkout date'
        : 'Dates selected';

  return (
    <div className="select-none">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-2">
        <button
          type="button"
          onClick={() => navigateMonth('prev')}
          disabled={!canGoPrev}
          className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-default transition-colors"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <span className="text-sm text-gray-500 hidden md:block">{phaseText}</span>

        <button
          type="button"
          onClick={() => navigateMonth('next')}
          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Calendar Grids */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <CalendarMonth
          year={baseMonth.year}
          month={baseMonth.month}
          getDayStatus={getDayStatus}
          onDayClick={handleDayClick}
          onDayHover={handleDayHover}
        />
        <CalendarMonth
          year={secondYear}
          month={secondMonth}
          getDayStatus={getDayStatus}
          onDayClick={handleDayClick}
          onDayHover={handleDayHover}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 px-2">
        <p className="text-sm text-gray-500 md:hidden">{phaseText}</p>
        {(checkIn || checkOut) && (
          <button
            type="button"
            onClick={clearDates}
            className="text-sm font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
          >
            Clear dates
          </button>
        )}
      </div>

      {minimumStay && minimumStay > 1 && (
        <p className="text-xs text-gray-500 mt-2 px-2">
          {minimumStay} night minimum
        </p>
      )}
    </div>
  );
};

export default DateRangeCalendar;
